import React, { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

interface ImageOptimizerProps {
  src: string;
  alt: string;
  className?: string;
  imgClassName?: string;
  width?: number;
  height?: number;
  priority?: boolean;
  quality?: number;
  sizes?: string;
  imgStyle?: React.CSSProperties;
  onLoad?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
  onError?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
}

export const ImageOptimizer: React.FC<ImageOptimizerProps> = ({
  src,
  alt,
  className,
  imgClassName,
  width,
  height,
  priority = false,
  quality = 85,
  sizes = "100vw",
  imgStyle,
  onLoad,
  onError
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const [error, setError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (priority) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px'
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [priority]);

  // Generate optimized image URLs
  const generateOptimizedUrl = (originalSrc: string, targetWidth?: number, targetQuality?: number, format: 'webp' | 'avif' = 'webp') => {
    // For external URLs, return as-is
    if (originalSrc.startsWith('http')) {
      return originalSrc;
    }

    // For Lovable uploads, we can add optimization parameters
    if (originalSrc.includes('lovable-uploads')) {
      const url = new URL(originalSrc, window.location.origin);
      if (targetWidth) url.searchParams.set('w', targetWidth.toString());
      if (targetQuality) url.searchParams.set('q', targetQuality.toString());
      url.searchParams.set('f', format);
      return url.toString();
    }

    return originalSrc;
  };

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    setIsLoaded(true);
    onLoad?.(e);
  };

  const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    setError(true);
    onError?.(e);
  };
  const shouldLoad = priority || isInView;
  const optimizedSrc = shouldLoad ? generateOptimizedUrl(src, width, quality) : undefined;

  // Generate srcSet for responsive images
  const generateSrcSet = (format: 'webp' | 'avif' = 'webp') => {
    if (!shouldLoad || error) return undefined;
    
    const breakpoints = [320, 640, 768, 1024, 1280, 1920];
    return breakpoints
      .filter(bp => !width || bp <= width * 2)
      .map(bp => `${generateOptimizedUrl(src, bp, quality, format)} ${bp}w`)
      .join(', ');
  };

  return (
    <div className={cn("relative", className)} ref={imgRef}>
      {/* Loading placeholder */}
      {!isLoaded && shouldLoad && (
        <div 
          className="absolute inset-0 bg-muted animate-pulse rounded"
          style={{ aspectRatio: width && height ? `${width}/${height}` : undefined }}
        />
      )}

      {/* Actual image */}
      {shouldLoad && (
        <picture>
          {/* AVIF source */}
          <source
            srcSet={generateSrcSet('avif')}
            sizes={sizes}
            type="image/avif"
          />
          {/* WebP source */}
          <source
            srcSet={generateSrcSet('webp')}
            sizes={sizes}
            type="image/webp"
          />
          
          {/* Fallback */}
          <img
            alt={alt}
            width={width}
            height={height}
            className={cn(
              imgClassName,
              "transition-opacity duration-300",
              isLoaded ? "opacity-100" : "opacity-0",
              error && "hidden"
            )}
            style={imgStyle}
            onLoad={handleLoad}
            onError={handleError}
            loading={priority ? "eager" : "lazy"}
            decoding="async"
            sizes={sizes}
            fetchPriority={priority ? "high" : undefined}
          />
        </picture>
      )}

      {/* Error fallback */}
      {error && (
        <div className="bg-muted rounded flex items-center justify-center text-muted-foreground text-sm p-4">
          Image unavailable
        </div>
      )}
    </div>
  );
};